import Building from "@/components/game/Building";
import { TileImage } from "@/components/game/TileImage";
import StreetTile from "@/components/game/StreetTile";
import { FloodWaves } from "@/components/game/TerrainBlock";
import { PLAYER_COLORS, PLAYER_SHADES } from "@/lib/api";
import { WATER } from "@/lib/terrain";

const FALLBACK_SHADE = { roof: "#B9B3A6", wall: "#8A8C85", edge: "#5F625C" };

export const pieceLabel = (p) =>
  p.kind === "cube"
    ? `${p.floors ?? (p.cube_stack || []).length}-cube building`
    : p.card_name || (p.card_key || "piece").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const nameOf = (players, color) => players.find((p) => p.color === color)?.name || "Climate Bank";

export const drownedText = (d, players) => `Lost to the sea in round ${d.round}: ${nameOf(players, d.owner)}'s ${pieceLabel(d)}`;

// A live board cell in the shape of a drowned record.
export const pieceOf = (cell) =>
  cell && (cell.kind === "tile" || cell.kind === "cube")
    ? { kind: cell.kind, card_key: cell.card_key, card_name: cell.card_name, owner: cell.owner, floors: (cell.cube_stack || []).length, token: cell.token }
    : null;

function PieceArt({ cellKey, piece }) {
  if (piece.kind === "tile") return <TileImage cardKey={piece.card_key} />;
  return (
    <>
      <StreetTile cellKey={cellKey} />
      <Building cellKey={cellKey} floors={piece.floors} shade={PLAYER_SHADES[piece.owner] || FALLBACK_SHADE} greenRoof={!!piece.token} riseFrom={piece.floors} />
    </>
  );
}

function GhostChip({ cellKey, color, bottom = 0 }) {
  return (
    <span data-testid={`drowned-chip-${cellKey}`} className="absolute pointer-events-none" style={{ left: 6, bottom: 6 + bottom, width: 12, height: 12, borderRadius: 2.5, background: PLAYER_COLORS[color], opacity: 0.4, zIndex: 3 }}>
      <svg viewBox="0 0 14 14" className="absolute inset-0" aria-hidden="true">
        <path d="M0 7.5 Q 2 5.5, 3.5 7.5 T 7 7.5 T 10.5 7.5 T 14 7.5" fill="none" stroke="var(--on-sea)" strokeWidth="1.3" />
      </svg>
    </span>
  );
}

// The square's old piece stays visible, then water rises over it from the sea edge.
function RisingWater({ cellKey, piece, delay }) {
  const t = { animationDelay: `${delay}ms` };
  return (
    <span data-testid={`sea-rise-${cellKey}`} className="absolute inset-0 pointer-events-none z-20">
      <span className="absolute inset-0 sea-rise-piece" style={{ ...t, background: piece ? undefined : "var(--cell-empty)" }}>
        {piece && <PieceArt cellKey={`${cellKey}-rise`} piece={piece} />}
      </span>
      <span className="absolute left-0 right-0 sea-rise-crest" style={t} />
    </span>
  );
}

// A wall that held: a short splash against its band at the sea edge.
export function WallSplash({ cellKey, delay }) {
  return (
    <svg data-testid={`wall-splash-${cellKey}`} className="absolute left-0 pointer-events-none z-30 wall-splash" style={{ bottom: -4, width: "100%", height: "38%", animationDelay: `${delay}ms` }} viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
      <path d="M4 40 Q 14 18, 24 32 Q 34 10, 46 30 Q 56 6, 66 30 Q 78 12, 86 32 Q 92 22, 96 40 Z" fill="var(--shallow)" opacity="0.85" />
      <circle cx="30" cy="9" r="2.4" fill="var(--on-sea)" />
      <circle cx="56" cy="4" r="2" fill="var(--on-sea)" />
      <circle cx="78" cy="9" r="2.2" fill="var(--on-sea)" />
    </svg>
  );
}

// A flooded square (3D BOARD R4): a faint ghost of the piece the sea took (if any) under the water
// overlay with three thin pale wave lines, the ghost owner chip and the tooltip. The block's side
// faces turn to water too (BoardGrid / TerrainBlock). `bottom`: px of the face hidden at the bottom.
export default function SeaSquare({ cellKey, drowned, rise, tip, players, bottom = 0 }) {
  return (
    <>
      {drowned && (
        <span data-testid={`drowned-ghost-${cellKey}`} className="absolute inset-0 pointer-events-none" style={{ opacity: 0.25, filter: "grayscale(0.6)" }}>
          <PieceArt cellKey={`${cellKey}-ghost`} piece={drowned} />
        </span>
      )}
      <span data-testid={`sea-marker-${cellKey}`} data-water="true" className="absolute inset-0 pointer-events-none" style={{ background: WATER.overlay, zIndex: 2 }}>
        <FloodWaves />
      </span>
      {drowned?.owner && <GhostChip cellKey={cellKey} color={drowned.owner} bottom={bottom} />}
      {rise && <RisingWater cellKey={cellKey} piece={rise.piece} delay={rise.delay} />}
      {tip && drowned && (
        <span
          role="tooltip"
          data-testid={`drowned-tip-${cellKey}`}
          className="absolute left-1/2 -translate-x-1/2 z-40 whitespace-nowrap rounded-md px-2 py-1 pointer-events-none"
          style={{ bottom: "calc(100% + 6px)", background: "var(--ink)", color: "var(--card)", fontSize: 12, fontWeight: 600 }}
        >
          {drownedText(drowned, players)}
        </span>
      )}
    </>
  );
}
